import { COVER_CROP, RDCoverCrop, TERMINATION_METHOD } from '@cibo/core'
import { boolean, object } from 'yup'
import { ITraitFeatureByIdYear } from '../types'
import { isDetailRequired } from '../useIsRequired'
import { findInitialValuesDetail } from '../utils'
import { CoverCropCell } from './CoverCropCell'
import { CoverCropCellEditor } from './CoverCropCellEditor'
import { CoverCropEditor } from './CoverCropEditor'
import { CoverCropResultsOnlyEditor } from './CoverCropResultsOnlyEditor'
import { CoverCropSimpleCell } from './CoverCropSimpleCell'

const TRAIT_ID = 'coverCrop'

const CoverCropBase: ITraitFeatureByIdYear<RDCoverCrop> = {
  traitId: TRAIT_ID,
  ns: '@cibo/landmanager/CoverCropEditor',
  cellDisplay: CoverCropCell,
  editor: CoverCropEditor,
  initialValues: () => false,
  SimpleCellView: CoverCropSimpleCell,
}

export const CoverCropInput: ITraitFeatureByIdYear<RDCoverCrop> = {
  ...CoverCropBase,

  initialValues: props => {
    const detail = findInitialValuesDetail(TRAIT_ID)(props)
    return !!detail?.input
      ? {
          ...detail.input,
        }
      : {
          crop: detail?.result === false ? 'none' : detail?.result === true ? 'other' : undefined,
        }
  },

  validJsonSchema: ({ requirement, t }) => {
    const requiredProps = [
      'plantedAt',
      'terminatedAt',
      'harvestedHay',
      'harvestedGrain',
      'terminationMethod',
    ].reduce<string[]>((acc, fieldName) => {
      if (isDetailRequired({ requirement, fieldName })) {
        acc.push(fieldName)
      }
      return acc
    }, [])

    return {
      type: 'object',
      properties: {
        crop: { enum: [...COVER_CROP] },
        plantedAt: {
          type: ['null', 'string'],
          format: 'date',
          dateBeforeField: 'terminatedAt',
          errorMessage: {
            dateBeforeField: t('plantedBeforeTerminated'),
          },
        },
        terminatedAt: { type: ['null', 'string'], format: 'date' },
        harvestedHay: { type: 'boolean' },
        harvestedGrain: { type: 'boolean' },
        terminationMethod: { enum: [...TERMINATION_METHOD] },
        acres: { type: 'number' },
      },
      required: ['crop'],
      if: {
        properties: { crop: { const: 'none' } },
      },
      then: {
        // no additional constraints for "no crop"
      },
      else: {
        errorMessage: {
          properties: {
            plantedAt: t('required', { context: 'plantedAt' }),
            terminatedAt: t('required', { context: 'terminatedAt' }),
            harvestedHay: t('required', { context: 'harvestedHay' }),
            harvestedGrain: t('required', { context: 'harvestedGrain' }),
            terminationMethod: t('required', { context: 'terminationMethod' }),
          },
        },
        required: requiredProps,
      },
    }
  },
}

export const CoverCropResult: ITraitFeatureByIdYear<RDCoverCrop> = {
  ...CoverCropBase,
  cellEditor: CoverCropCellEditor,
  editor: CoverCropResultsOnlyEditor,

  // @ts-ignore complicated to make this work for results only
  initialValues: props => {
    const detail = findInitialValuesDetail(TRAIT_ID)(props)

    return typeof detail?.result !== 'undefined'
      ? {
          result: detail.result === true ? 'true' : 'false',
        }
      : {
          result: '',
        }
  },

  // @ts-ignore complicated to make this work for results only
  validationSchema: ({ t }) =>
    object({
      result: boolean().required(t('required')),
    }),
  detailFromEditorValues: (formValues, requirement) => ({
    traitId: TRAIT_ID,
    year: requirement?.year,
    result: formValues.input.result,
  }),
}
